// waAuth — iter103. Tiny façade around the `whatsapp_auth_enabled` admin
// setting so every component can read/respect it identically.
//
// Contract:
//   - Cached in localStorage as "habano.wa_auth_enabled" ("true" | "false").
//   - If no cached value exists, default to FALSE (Google only). This is
//     deliberately conservative — we never want to render the WhatsApp pill
//     before the public-settings call has confirmed it's allowed.
//   - On every app load, `refreshWaAuthEnabled()` fetches /public-settings,
//     updates the cache, and emits the "habano:wa-auth-changed" CustomEvent
//     so subscribed components re-render.
//
// Reading the value:
//   const enabled = useWaAuthEnabled();        // hook (auto re-render)
//   const enabled = getWaAuthEnabled();        // synchronous getter
//
// Writing the value (admin toggle):
//   await setWaAuthEnabled(true);              // PUT /admin/settings + cache.
import { useEffect, useState } from "react";
import axios from "axios";

const KEY = "habano.wa_auth_enabled";
const EVT = "habano:wa-auth-changed";
const BASE = process.env.REACT_APP_BACKEND_URL;

function readCache() {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "true") return true;
    if (v === "false") return false;
    return null;
  } catch { return null; }
}
function writeCache(b) {
  try { localStorage.setItem(KEY, b ? "true" : "false"); } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: { enabled: b } }));
  } catch { /* ignore */ }
}

/** Synchronous getter — returns the cached value, or FALSE if no cache exists.
 *  Conservative default ensures the WhatsApp pill never flashes before the
 *  public-settings call confirms it's allowed. */
export function getWaAuthEnabled() {
  const cached = readCache();
  return cached === null ? false : cached;
}

/** Fetch /public-settings, update the cache, and notify subscribers. */
export async function refreshWaAuthEnabled() {
  try {
    const { data } = await axios.get(`${BASE}/api/public-settings`, { timeout: 6000 });
    const raw = (data && data.whatsapp_auth_enabled) || "true";
    const enabled = String(raw).toLowerCase() === "true";
    writeCache(enabled);
    return enabled;
  } catch {
    // Network failure — keep the cached value if it exists; otherwise leave
    // the conservative FALSE default so we never accidentally surface
    // WhatsApp auth when the toggle could not be confirmed.
    return readCache() === true;
  }
}

/** Admin-side setter — persists via the admin/settings PUT endpoint and
 *  immediately updates the cache so the toggle reflects new state without
 *  needing a refresh. The caller must pass an authed axios instance OR the
 *  request will fail; we keep this simple by delegating to whichever axios
 *  the caller has. */
export async function setWaAuthEnabled(api, enabled) {
  await api.put("/admin/settings/whatsapp_auth_enabled", {
    value: enabled ? "true" : "false",
  });
  writeCache(enabled);
  return enabled;
}

/** React hook — reactive read of the cached value. */
export function useWaAuthEnabled() {
  const [enabled, setEnabled] = useState(getWaAuthEnabled);
  useEffect(() => {
    const handle = (e) => {
      // CustomEvent path
      if (e && e.detail && typeof e.detail.enabled === "boolean") {
        setEnabled(e.detail.enabled);
        return;
      }
      // Storage event path (other tabs / SW)
      setEnabled(getWaAuthEnabled());
    };
    window.addEventListener(EVT, handle);
    window.addEventListener("storage", handle);
    return () => {
      window.removeEventListener(EVT, handle);
      window.removeEventListener("storage", handle);
    };
  }, []);
  return enabled;
}
